// pages/CapsuleTiles.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const rarityColors = {
  0: '#4C6AFF',
  1: '#8948FF',
  2: '#D32DE6',
  3: '#EB4C4C',
  4: '#FFD700',
};

const CapsuleTiles = () => {
  const [capsules, setCapsules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_SERVER_URI}/api/capsules`)
      .then((res) => {
        console.log('✅ Capsule response:', res.data);
        setCapsules(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('❌ Error fetching capsules:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-xl text-gray-300">Loading capsules...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header Section */}
      <div className="relative pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 bg-clip-text text-transparent">
              Sticker Capsules
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Explore sticker capsules and calculate your odds for getting the stickers you want
            </p>
          </div>
        </div>
      </div>

      {/* Capsules Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...capsules].reverse().map((capsule) => (
            <Link
              key={capsule._id}
              to={`/capsules/${encodeURIComponent(capsule.name)}`}
              className="group relative bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 hover:border-orange-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              {/* Capsule Image */}
              <div className="relative p-6">
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <img
                      src={capsule.imageUrl}
                      alt={capsule.name}
                      className="w-32 h-32 object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                    {/* Glow effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>

                {/* Capsule Name */}
                <h2 className="text-xl font-bold text-center mb-3 text-white group-hover:text-orange-400 transition-colors duration-200">
                  {capsule.name}
                </h2>

                {/* Price Information */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Capsule Price:</span>
                    <span className="text-green-400 font-semibold">${capsule.casePrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Key Price:</span>
                    <span className="text-blue-400 font-semibold">${capsule.priceToOpen.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-t border-gray-700/50 pt-2">
                    <span className="text-gray-300 font-medium">Total Cost:</span>
                    <span className="text-orange-400 font-bold">${(capsule.casePrice + capsule.priceToOpen).toFixed(2)}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="mt-4">
                  <div className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-center py-3 px-4 rounded-lg font-semibold group-hover:from-orange-600 group-hover:to-red-600 transition-all duration-200 transform group-hover:scale-105">
                    Calculate Odds
                  </div>
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {capsules.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No Capsules Available</h3>
            <p className="text-gray-400">Check back later for available capsules.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapsuleTiles;
